import React, { memo } from "react";
import moment from "moment";
import { Role } from "@teamrota/authlib";
import {
  RotaButtonNew,
  RotaDivider,
  RotaCalendar,
  RotaSearchInput
} from "@teamrota/rota-design";

import HasRole from "~/src/containers/has-role";

// scheduler references
import PublishContainer from "~/src/containers/scheduler/PublishContainer";
import {
  Heading,
  Row,
  StyledToolBarLeft,
  StyledToolBarRight,
  PublishContainerWrapper,
  StyledFilterContainer,
  ButtonsWrapper,
  StatsWrapper,
  Stats
} from "./calendar-header.styles";

import FilterDropdown from "./FilterDropdown/filter-dropdown";
import SortDropdown from "./SortDropdown/sort-dropdown";

const CalendarHeader = ({
  setStartDate,
  setIsShiftCreateModalOpen,
  countOfDraftShifts,
  draftShiftIds,
  refetchSchedules,
  handlePartnerSelect,
  partners,
  setRoleIds,
  setVenueIds,
  isShowCancelledShifts,
  setIsShowCancelledShifts,
  setSelectedMemberTypes,
  setSelectedMemberInfo,
  setSelectedUserIds,
  schedule,
  setSearchTerm,
  setSortBy,
  sortBy
}) => {
  const totalMembersCount = schedule?.totalMembersCount || 0;
  const unscheduledBookings = schedule?.unassignedShifts.reduce(
    (acc, day) => {
      const groupIds = [];
      const unfilledCount = day.shifts.reduce((accShift, shift) => {
        //here we are excluding count for linked shift (if there are two linked shifts that require 3 members each - the count is 3 rather than 2x3)
        if (shift.shiftGroupId && acc.groupIds.includes(shift.shiftGroupId)) {
          return accShift;
        } else {
          if (shift.shiftGroupId) groupIds.push(shift.shiftGroupId);
          return (
            accShift + (shift.numberRequested - shift.bookings.totalResults)
          );
        }
      }, 0);
      return {
        count: acc.count + unfilledCount,
        groupIds: [...acc.groupIds, ...groupIds]
      };
    },
    { count: 0, groupIds: [] }
  ) || { count: 0, groupIds: [] };

  const scheduledBookings = schedule?.scheduledMembers.reduce(
    (acc, member) => {
      const groupIds = [];
      const bookingsCount = member.scheduledDays.reduce((acc, day) => {
        let count = 0;
        day.bookings?.forEach(booking => {
          if (
            booking?.shift?.shiftGroupId &&
            (acc?.groupIds?.includes(booking.shift.shiftGroupId) ||
              groupIds.includes(booking.shift.shiftGroupId))
          ) {
            return;
          } else {
            if (booking.shift.shiftGroupId)
              groupIds.push(booking.shift.shiftGroupId);
            count++;
          }
        });
        return acc + count;
      }, 0);
      return {
        count: acc.count + bookingsCount,
        groupIds: [...acc.groupIds, ...groupIds]
      };
    },
    { count: 0, groupIds: [] }
  ) || { count: 0, groupIds: [] };

  const totalBookings = unscheduledBookings.count + scheduledBookings.count;
  const fulfillment = (
    (scheduledBookings?.count / totalBookings || 0) * 100
  ).toFixed(1);

  return (
    <Heading>
      <Row withBorder>
        <StatsWrapper>
          <Stats>
            <span>{totalMembersCount}</span>
            {"members"}
          </Stats>
          <RotaDivider vertical height="100%" />
          <Stats>
            <span>{totalBookings}</span>
            {"bookings"}
          </Stats>
          <RotaDivider vertical height="100%" />
          <Stats>
            <span>{scheduledBookings.count}</span>
            {"bookings filled"}
          </Stats>
          <RotaDivider vertical height="100%" />
          <Stats>
            <span>{unscheduledBookings.count}</span>
            {"unfilled bookings"}
          </Stats>
          <RotaDivider vertical height="100%" />
          <Stats>
            <span>{fulfillment}%</span>
            {"fulfilment"}
          </Stats>
        </StatsWrapper>
        <ButtonsWrapper>
          <HasRole role={Role.SHIFTS_CREATE}>
            <RotaButtonNew
              variant="outlined"
              onClick={() => setIsShiftCreateModalOpen(true)}
            >
              Create Shift
            </RotaButtonNew>
          </HasRole>
          <PublishContainerWrapper>
            <PublishContainer
              countOfDraftShifts={countOfDraftShifts}
              draftShiftIds={draftShiftIds}
              refetchSchedules={refetchSchedules}
            />
          </PublishContainerWrapper>
        </ButtonsWrapper>
      </Row>
      <Row>
        <StyledToolBarLeft>
          <RotaSearchInput onChange={value => setSearchTerm(value)} />
          <SortDropdown setSortBy={setSortBy} sortBy={sortBy} />
          <StyledFilterContainer>
            <FilterDropdown
              partners={partners}
              handlePartnerSelect={handlePartnerSelect}
              setRoleIds={setRoleIds}
              setVenueIds={setVenueIds}
              isShowCancelledShifts={isShowCancelledShifts}
              setIsShowCancelledShifts={setIsShowCancelledShifts}
              setSelectedMemberTypes={setSelectedMemberTypes}
              setSelectedMemberInfo={setSelectedMemberInfo}
              setSelectedUserIds={setSelectedUserIds}
            />
          </StyledFilterContainer>
        </StyledToolBarLeft>

        <StyledToolBarRight>
          <RotaCalendar
            onChange={({ date }) => {
              setStartDate(moment(date).startOf("day"));
            }}
          />
        </StyledToolBarRight>
      </Row>
    </Heading>
  );
};

export default memo(CalendarHeader);
